import { NavLink, Outlet } from "react-router-dom";
import {
  CalendarCheck2,
  ClipboardList,
  LayoutDashboard,
  Megaphone,
  Menu,
  PackageCheck,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/field";
import { useAuth } from "@/providers/auth";
import { cn } from "@/lib/utils";

const items = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/profiles", label: "Profiles", icon: UsersRound },
  {
    to: "/admin/distributions",
    label: "Distributions",
    icon: PackageCheck,
  },
  { to: "/admin/forms", label: "Forms", icon: ClipboardList },
  { to: "/admin/events", label: "Events", icon: CalendarCheck2 },
  { to: "/admin/communications", label: "Communications", icon: Megaphone },
  { to: "/admin/users", label: "Users", icon: ShieldCheck },
];

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailRequestId, setEmailRequestId] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const auth = useAuth();

  const requestEmailCode = async () => {
    setEmailBusy(true);
    try {
      const result = await auth.requestEmailCode(email);
      setEmailRequestId(result.request_id);
      setEmailMessage(result.message);
      setCode("");
    } catch {
      // AuthProvider exposes the user-facing error.
    } finally {
      setEmailBusy(false);
    }
  };

  const verifyEmailCode = async () => {
    setEmailBusy(true);
    try {
      await auth.verifyEmailCode(email, emailRequestId, code);
    } catch {
      // AuthProvider exposes the user-facing error.
    } finally {
      setEmailBusy(false);
    }
  };
  if (auth.loading)
    return <div className="grid min-h-screen place-items-center">Loading…</div>;
  if (import.meta.env.PROD && !auth.configured) {
    return (
      <div className="grid min-h-screen place-items-center bg-muted p-5">
        <div className="max-w-md rounded-2xl border bg-white p-8 text-center shadow-soft">
          <h1 className="text-2xl font-bold">Admin unavailable</h1>
          <p className="mt-2 text-muted-foreground">
            Firebase Authentication is not configured for this deployment.
          </p>
        </div>
      </div>
    );
  }
  if (auth.configured && !auth.user) {
    return (
      <div className="grid min-h-screen place-items-center bg-muted p-5">
        <div className="max-w-md rounded-2xl border bg-white p-8 text-center shadow-soft">
          <img
            src="/brand/hope-hangar-logo-black.png"
            alt="Hope Hangar"
            className="mx-auto h-12 w-auto"
          />
          <h1 className="mt-5 text-2xl font-bold">Hope Hangar Admin</h1>
          <p className="mt-2 text-muted-foreground">
            Sign in with an authorized admin account.
          </p>
          {auth.error && (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800"
            >
              {auth.error}
            </p>
          )}
          <Button
            className="mt-6 w-full"
            onClick={() => void auth.signInWithGoogle()}
          >
            Sign in with Google
          </Button>
          <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
          {!emailRequestId ? (
            <form
              className="space-y-3 text-left"
              onSubmit={(event) => {
                event.preventDefault();
                void requestEmailCode();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button
                className="w-full"
                type="submit"
                variant="outline"
                disabled={emailBusy || !email.trim()}
              >
                {emailBusy ? "Sending…" : "Email me a sign-in code"}
              </Button>
            </form>
          ) : (
            <form
              className="space-y-3 text-left"
              onSubmit={(event) => {
                event.preventDefault();
                void verifyEmailCode();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="admin-email-code">Six-digit code</Label>
                <Input
                  id="admin-email-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.replace(/\D/g, ""))
                  }
                  placeholder="000000"
                  className="text-center font-mono tracking-[0.35em]"
                />
              </div>
              {emailMessage && (
                <p className="text-sm text-muted-foreground" role="status">
                  {emailMessage}
                </p>
              )}
              <Button
                className="w-full"
                type="submit"
                disabled={emailBusy || code.length !== 6}
              >
                {emailBusy ? "Verifying…" : "Verify and sign in"}
              </Button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  className="text-muted-foreground underline underline-offset-4"
                  onClick={() => {
                    setEmailRequestId("");
                    setEmailMessage("");
                    setCode("");
                  }}
                >
                  Change email
                </button>
                <button
                  type="button"
                  className="text-muted-foreground underline underline-offset-4"
                  disabled={emailBusy}
                  onClick={() => void requestEmailCode()}
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }
  if (auth.configured && auth.user && !auth.admin) {
    return (
      <div className="grid min-h-screen place-items-center bg-muted p-5">
        <div className="max-w-md rounded-2xl border bg-white p-8 text-center shadow-soft">
          <img
            src="/brand/hope-hangar-logo-black.png"
            alt="Hope Hangar"
            className="mx-auto h-12 w-auto"
          />
          <h1 className="mt-5 text-2xl font-bold">Admin access required</h1>
          <p className="mt-2 text-muted-foreground">
            Your account is signed in but does not have Hope Hangar
            administrator access.
          </p>
          <Button
            className="mt-6"
            variant="outline"
            onClick={() => void auth.signOutUser()}
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-muted/40 lg:grid lg:grid-cols-[260px_1fr]">
      <aside
        className={cn(
          "fixed inset-y-0 z-30 w-[260px] -translate-x-full border-r bg-primary p-5 text-primary-foreground transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
          open && "translate-x-0",
        )}
      >
        <div className="px-2 py-3">
          <img
            src="/brand/hope-hangar-logo-white.png"
            alt="Hope Hangar"
            className="h-10 w-auto"
          />
          <span className="mt-2 block text-xs font-medium uppercase tracking-[0.16em] text-white/60">
            Admin beta
          </span>
        </div>
        <nav className="mt-8 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white",
                  isActive && "bg-white/15 text-white",
                )
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-6 left-5 right-5 rounded-lg bg-white/10 p-3 text-xs text-white/70">
          Mountain View pilot
          <br />
          Outbound delivery is gated
        </div>
      </aside>
      <div className="min-w-0">
        <header className="flex h-16 items-center justify-between border-b bg-white px-5 lg:px-8">
          <Button
            size="icon"
            variant="ghost"
            className="lg:hidden"
            onClick={() => setOpen(!open)}
          >
            <Menu size={20} />
            <span className="sr-only">Open menu</span>
          </Button>
          <p className="hidden text-sm text-muted-foreground sm:block">
            Hope Hangar operations
          </p>
          <div className="flex items-center gap-3 text-sm">
            <span>{auth.user?.email || "Local administrator"}</span>
            {auth.user && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void auth.signOutUser()}
              >
                Sign out
              </Button>
            )}
          </div>
        </header>
        <main className="p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
